export interface Point { x: number; y: number; }
export interface Footprint { w: number; h: number; }
/** A desk position plus the way its customers stand (default south, the original floor plan). */
export interface Pose extends Point { facing?: readonly number[] | [number, number]; }

/** Desk-local offset to world: `side` runs across the counter (east for a south-facing desk), `out` toward customers. */
export function at(s: Pose, side: number, out: number): Point {
    const fx = s.facing?.[0] ?? 0, fy = s.facing?.[1] ?? 1;
    if (fx === 0 && fy === 1) return { x: s.x + side, y: s.y + out };
    return { x: s.x + side * fy + out * fx, y: s.y - side * fx + out * fy };
}

/** Parameters keep build validation and runtime wrappers on the same placement rules. */
export function stationPad(stages: readonly Pose[], footprints: readonly Footprint[], index: number, kind: string): Point {
    const s = stages[index];
    if (kind === 'work') return at(s, 0, -footprints[index].h / 2 - 32);
    if (kind === 'build') return at(s, index === 1 ? 130 : 0, 110);
    if (index === 6 && kind === 'upgrade') return at(s, 140, 160);
    // Stacked 65 apart along the desk's depth; a side-facing desk stacks them horizontally, where a pad is 68 wide.
    const management = index === 1 ? 160 : 35, step = s.facing?.[0] ? 72 : 65;
    if (kind === 'hire') return at(s, 130, management);
    if (kind === 'upgrade') return at(s, 130, management + step);
    return at(s, 130, management + step * 2);
}

export function queuePoint(stages: readonly Pose[], footprints: readonly Footprint[], index: number, rank: number): Point {
    const s = stages[index], column = Math.floor(rank / 3), row = rank % 3;
    if (index === 6) return at(s, -(rank % 6) * 42, footprints[index].h / 2 + 66 + Math.floor(rank / 6) * 42);
    return at(s, -column * 52, footprints[index].h / 2 + 66 + (column % 2 ? 2 - row : row) * 40);
}

export function servicePoint(stages: readonly Pose[], footprints: readonly Footprint[], index: number, slot: number, lanes = 1): Point {
    return at(stages[index], (slot - (lanes - 1) / 2) * 32, footprints[index].h / 2 + 32);
}

export function seatFacing(sofa: Point): number { return sofa.y === 680 ? -1 : 1; }

export function bounds(rect: Point & Footprint) {
    return { left: rect.x - rect.w / 2, right: rect.x + rect.w / 2, top: rect.y - rect.h / 2, bottom: rect.y + rect.h / 2 };
}

export interface Rect extends Point { w: number; h: number; }
/** A map expansion: its gates (with a boarded door each), the area it is reached through (-1: the floor) and its hoarding. */
export interface ExpansionArea { gates: readonly number[]; doors: readonly Rect[]; parent: number; hoarding: readonly Rect[]; }
/**
 * Which expansions are open with `gates` bought. One opens with its own gate, and also when an area reached through it
 * opens: the way in has to be clear, so an outer area becomes a corridor (its own doors stay boarded).
 */
export function expansionsOpen(areas: readonly ExpansionArea[], gates: number): boolean[] {
    const open = areas.map(x => x.gates.some(gate => gate < gates));
    areas.forEach((x, i) => { if (open[i]) for (let p = x.parent; p >= 0; p = areas[p].parent) open[p] = true; });
    return open;
}
/** The first gate that opens an expansion: its own, or one in any area reached through it. */
export function expansionOpensWith(areas: readonly ExpansionArea[], index: number): number {
    const through = areas.flatMap((x, i) => i !== index && reachedThrough(areas, i, index) ? x.gates : []);
    return Math.min(...areas[index].gates, ...through);
}
function reachedThrough(areas: readonly ExpansionArea[], index: number, via: number): boolean {
    for (let p = areas[index].parent; p >= 0; p = areas[p].parent) if (p === via) return true;
    return false;
}
/** Hoarding still standing with `gates` bought: closed areas' walls plus every unbought gate's boarded door. */
export function standingHoarding(areas: readonly ExpansionArea[], gates: number): Rect[] {
    const open = expansionsOpen(areas, gates), seen = new Set<string>(), out: Rect[] = [];
    areas.forEach((x, i) => {
        for (const r of [...(open[i] ? [] : x.hoarding), ...x.doors.filter((_, k) => x.gates[k] >= gates)]) {
            // Two areas side by side share the wall between them; it stands once.
            const key = [r.x, r.y, r.w, r.h].map(v => Math.round(v)).join(',');
            if (!seen.has(key)) { seen.add(key); out.push(r); }
        }
    });
    return out;
}
